import { useState, useRef, useCallback } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  useDataProvider,
  useRefresh,
} from 'react-admin';
import {
  Button,
  Paper,
  Box,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { getToken } from '../authentication/authProvider';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Helper to format cents to dollars
const formatCentsToDollars = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return 'N/A';
  return `$${(cents / 100).toFixed(2)}`;
};

// Helper component to display processing status with color coding
const ProcessingStatusField = ({ source }: { source: string }) => (
  <FunctionField
    source={source}
    render={(record: any) => {
      const status = record[source];
      const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
        completed: 'success',
        processing: 'warning',
        failed: 'error',
        pending: 'default',
      };
      return (
        <Chip
          label={status || 'unknown'}
          color={colorMap[status] || 'default'}
          size="small"
        />
      );
    }}
  />
);

interface LogLine {
  timestamp: string;
  id: string;
  message: string;
  color: string;
}

const RetryAllButton = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState<LogLine[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const logRef = useRef<HTMLDivElement>(null);

  const addLog = useCallback((id: string, message: string, color: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, id, message, color }]);
    setTimeout(() => {
      if (logRef.current) {
        logRef.current.scrollTop = logRef.current.scrollHeight;
      }
    }, 50);
  }, []);

  const handleRetryAll = async () => {
    setRunning(true);
    setLogs([]);
    setProgress({ current: 0, total: 0 });

    try {
      // Fetch all stripe payments
      const { data } = await dataProvider.getList('sb_payments', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { payment_source: 'stripe' },
      });

      // Filter for non-completed
      const failed = data.filter(
        (r: any) => r.processing_status !== 'completed'
      );

      if (failed.length === 0) {
        addLog('-', 'No failed payments found', '#4caf50');
        setRunning(false);
        return;
      }

      setProgress({ current: 0, total: failed.length });
      addLog('-', `Found ${failed.length} non-completed payments to retry`, '#ffffff');

      const token = await getToken();

      for (let i = 0; i < failed.length; i++) {
        const payment = failed[i];
        const paymentId = payment.stripe_payment_intent_id || payment.stripe_session_id;
        setProgress({ current: i + 1, total: failed.length });
        addLog(payment.id, `Retrying (${i + 1}/${failed.length})...`, '#ffffff');

        try {
          let url: string;
          if (payment.stripe_payment_intent_id) {
            url = `${PAPERMAN_API_URL}stripe/retry-payment-intent?payment_intent_id=${payment.stripe_payment_intent_id}`;
          } else if (payment.stripe_session_id) {
            url = `${PAPERMAN_API_URL}stripe/retry-payment?session_id=${payment.stripe_session_id}`;
          } else {
            addLog(payment.id, 'No stripe ID found, skipping', '#ff9800');
            continue;
          }

          const res = await fetch(url, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.ok) {
            const body = await res.json();
            addLog(payment.id, `Success: ${body.message || 'OK'}`, '#4caf50');
          } else {
            const body = await res.json().catch(() => ({}));
            addLog(
              payment.id,
              `Error ${res.status}: ${body.details || body.message || body.error || res.statusText}`,
              '#f44336'
            );
          }
        } catch (err: any) {
          addLog(payment.id, `Fetch error: ${err.message}`, '#f44336');
        }

        // 1s delay between calls
        if (i < failed.length - 1) {
          await new Promise((r) => setTimeout(r, 1000));
        }
      }

      addLog('-', 'Retry complete', '#4caf50');
      refresh();
    } catch (err: any) {
      addLog('-', `Failed to fetch payments: ${err.message}`, '#f44336');
    } finally {
      setRunning(false);
    }
  };

  const progressPercent =
    progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <Box mb={2}>
      <Button
        variant="contained"
        color="warning"
        startIcon={<ReplayIcon />}
        onClick={handleRetryAll}
        disabled={running}
        sx={{ mb: 1 }}
      >
        {running
          ? `Retrying ${progress.current}/${progress.total}...`
          : 'Retry All Failed'}
      </Button>

      {logs.length > 0 && (
        <Paper
          ref={logRef}
          sx={{
            bgcolor: '#1e1e1e',
            p: 2,
            maxHeight: 300,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            mt: 1,
          }}
        >
          {running && (
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ mb: 1 }}
            />
          )}
          {logs.map((log, i) => (
            <Typography
              key={i}
              component="div"
              sx={{
                color: log.color,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                lineHeight: 1.6,
              }}
            >
              [{log.timestamp}] #{log.id} {log.message}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
};

const StripePaymentsList = () => (
  <List
    resource="sb_payments"
    filter={{ payment_source: 'stripe' }}
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
    actions={false}
  >
    <RetryAllButton />
    <Datagrid bulkActionButtons={false}>
      <TextField source="id" label="ID" />
      <TextField source="contract_id" label="Contract" />
      <FunctionField
        label="Stripe"
        render={(record: any) => {
          const stripeId =
            record.stripe_payment_intent_id || record.stripe_session_id;
          if (!stripeId) return 'N/A';

          const isLive =
            stripeId.startsWith('cs_live_') ||
            stripeId.startsWith('pi_live_') ||
            // production payment intents don't have _live_ prefix
            (stripeId.startsWith('pi_') && !stripeId.startsWith('pi_test_'));
          const baseUrl = isLive
            ? 'https://dashboard.stripe.com/payments'
            : 'https://dashboard.stripe.com/test/payments';
          const url = `${baseUrl}/${stripeId}`;
          const label = record.stripe_payment_intent_id
            ? 'Payment Intent'
            : 'Checkout Session';

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#635BFF',
                textDecoration: 'none',
                fontWeight: 500,
              }}
              title={`${label}: ${stripeId}`}
            >
              View in Stripe ↗
            </a>
          );
        }}
      />
      <FunctionField
        label="Amount"
        render={(record: any) => formatCentsToDollars(record.amount_total)}
      />
      <FunctionField
        label="Stripe Fee"
        render={(record: any) =>
          formatCentsToDollars(record.stripe_transaction_fee)
        }
      />
      <TextField source="payment_status" label="Payment Status" />
      <ProcessingStatusField source="processing_status" />
      <TextField source="quickbooks_sales_receipt_id" label="QB Receipt ID" />
      <TextField source="error_message" label="Error" />
      <FunctionField
        label="Created"
        render={(record: any) => {
          if (!record.created_at) return 'N/A';
          try {
            return new Date(record.created_at).toLocaleString();
          } catch {
            return 'Invalid date';
          }
        }}
      />
      <FunctionField
        label="Processed"
        render={(record: any) => {
          if (!record.processed_at) return '-';
          try {
            return new Date(record.processed_at).toLocaleString();
          } catch {
            return 'Invalid date';
          }
        }}
      />
    </Datagrid>
  </List>
);

export default StripePaymentsList;
